import { API_URL } from '../utils/api';
import { useState, useEffect } from 'react';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import {
  Buildings, GraduationCap, Storefront, Users, UserCircle, Globe, ArrowsClockwise, ArrowUpRight,
} from '@phosphor-icons/react';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend,
} from 'recharts';
import { SkBar, SkeletonTable } from '../components/ui/page-skeleton';

const fmtInt = (n) => Number(n || 0).toLocaleString();

export default function SuperDashboardPage() {
  const { isSuperAdmin } = useAuth();
  const [overview, setOverview] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState('');
  const [entitiesPage, setEntitiesPage] = useState(1);
  const ENTITIES_PAGE_SIZE = 5;

  const fetchOverview = async (silent = false) => {
    if (!silent) setLoading(true);
    setError('');
    try {
      const token = localStorage.getItem('access_token');
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      const { data } = await axios.get(`${API_URL}/api/super-dashboard/overview`, { withCredentials: true, headers });
      setOverview(data);
    } catch (e) {
      setError(e?.response?.data?.detail || e.message || 'Failed to load overview');
      setOverview(null);
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  useEffect(() => { fetchOverview(); }, []);
  useEffect(() => { setEntitiesPage(1); }, [overview?.entities?.length]);

  if (!isSuperAdmin) {
    return (
      <div className="bg-[#FFFFFF] border border-[#EDE6D8] rounded-2xl p-8 text-center" data-testid="super-dashboard-denied">
        <p className="text-[#78716C]">Access denied. Super admin privileges required.</p>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="space-y-6" data-testid="super-dashboard-loading">
        {/* Header skeleton */}
        <div className="flex items-end justify-between">
          <SkBar className="h-8 w-52" />
          <SkBar className="h-8 w-28 rounded-full" />
        </div>
        {/* 5-stat grid skeleton */}
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4">
          {[0,1,2,3,4].map(i => (
            <div key={i} className="rounded-2xl border border-[#EDE6D8] bg-white p-4">
              <SkBar className="h-9 w-9 rounded-xl mb-2" />
              <SkBar className="h-7 w-2/3 mb-1" />
              <SkBar className="h-3 w-1/2" />
            </div>
          ))}
        </div>
        {/* Charts + entities table skeleton */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <div className="rounded-2xl border border-[#EDE6D8] bg-white p-6 h-72"><SkBar className="h-full w-full" /></div>
          <div className="rounded-2xl border border-[#EDE6D8] bg-white p-6 h-72"><SkBar className="h-full w-full" /></div>
        </div>
        <SkeletonTable rows={5} cols={6} />
      </div>
    );
  }

  const totals = overview?.totals || {};
  const deltas = overview?.deltas_30d || {};
  const regional = overview?.regional || [];
  const entities = overview?.entities || [];
  const entitiesTotalPages = Math.max(1, Math.ceil(entities.length / ENTITIES_PAGE_SIZE));
  const entitiesPageSafe = Math.min(entitiesPage, entitiesTotalPages);
  const entitiesStart = (entitiesPageSafe - 1) * ENTITIES_PAGE_SIZE;
  const entitiesEnd = entitiesStart + ENTITIES_PAGE_SIZE;
  const entitiesPageRows = entities.slice(entitiesStart, entitiesEnd);

  const onRefresh = () => { setRefreshing(true); fetchOverview(true); };

  const globalStats = [
    { key: 'entities',    label: 'Entities',    value: totals.entities,    delta: deltas.entities,    icon: Buildings,    color: '#0A2540' },
    { key: 'campuses',    label: 'Campuses',    value: totals.campuses,    delta: deltas.campuses,    icon: GraduationCap, color: '#1E3A5F' },
    { key: 'stores',      label: 'Stores',      value: totals.stores,      delta: deltas.stores,      icon: Storefront,   color: '#10B981' },
    { key: 'subscribers', label: 'Subscribers', value: totals.subscribers, delta: deltas.subscribers, icon: Users,        color: '#B88E14' },
    { key: 'users',       label: 'Users',       value: totals.users,       delta: deltas.users,       icon: UserCircle,   color: '#EF4444' },
  ];

  // Top 5 entities by stores count (drop zero-store entities for a cleaner top list)
  const topStoresByEntity = entities
    .filter((e) => (e.stores_count || 0) > 0)
    .slice(0, 5);
  const topStoresTotal = topStoresByEntity.reduce((s, e) => s + (e.stores_count || 0), 0);

  return (
    <div className="space-y-6" data-testid="super-dashboard-page">
      {/* Header */}
      <div className="flex items-end justify-between flex-wrap gap-2">
        <div>
          <h1 className="text-3xl font-bold text-[#1C1917] tracking-tight" style={{ fontFamily: 'Plus Jakarta Sans, sans-serif' }}>
            Super Dashboard
          </h1>
        </div>
        <button
          onClick={onRefresh}
          disabled={refreshing}
          data-testid="super-dashboard-refresh"
          className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-white border border-[#EDE6D8] text-[#0A2540] text-[11px] font-semibold hover:bg-slate-50 transition-colors disabled:opacity-60"
          style={{ fontFamily: 'JetBrains Mono, monospace' }}
        >
          <ArrowsClockwise size={12} weight="bold" className={refreshing ? 'animate-spin' : ''} />
          REFRESH
        </button>
      </div>

      {error && (
        <div className="rounded-xl border border-rose-200 bg-rose-50/60 text-rose-700 text-sm px-4 py-3" data-testid="super-dashboard-error">{error}</div>
      )}

      {/* Global Stats */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4" data-testid="global-stats">
        {globalStats.map((stat, index) => {
          const Icon = stat.icon;
          const delta = Number(stat.delta || 0);
          return (
            <div
              key={stat.key}
              className="relative overflow-hidden bg-[#FFFFFF] border border-[#EDE6D8] rounded-2xl p-4 hover:-translate-y-0.5 transition-transform duration-200 shadow-[0_1px_3px_rgba(15,23,42,0.04)]"
              data-testid={`global-stat-${stat.key}`}
            >
              <span className="absolute left-0 top-0 h-full w-[3px]" style={{ backgroundColor: stat.color }} />
              <div className="flex items-center justify-between mb-2">
                <div className="p-2 rounded-xl" style={{ backgroundColor: `${stat.color}15` }}>
                  <Icon size={20} style={{ color: stat.color }} weight="duotone" />
                </div>
                {delta > 0 && (
                  <span className="px-1.5 py-0.5 rounded-md bg-emerald-50 text-emerald-700 text-[10px] font-semibold" style={{ fontFamily: 'JetBrains Mono, monospace' }}>
                    +{delta} · 30d
                  </span>
                )}
              </div>
              <p className="text-2xl font-bold text-[#1C1917]" style={{ fontFamily: 'JetBrains Mono, monospace', color: stat.color }}>{fmtInt(stat.value)}</p>
              <p className="text-[10px] text-[#78716C] uppercase tracking-wider mt-1" style={{ fontFamily: 'JetBrains Mono, monospace' }}>{stat.label}</p>
            </div>
          );
        })}
      </div>

      {/* Charts Row */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Regional Distribution */}
        <div className="bg-[#FFFFFF] border border-[#EDE6D8] rounded-2xl p-6 shadow-[0_1px_3px_rgba(15,23,42,0.04)]" data-testid="regional-distribution">
          <h2 className="text-sm font-semibold text-[#0A2540] tracking-tight mb-1" style={{ fontFamily: 'Plus Jakarta Sans, sans-serif' }}>Regional Distribution</h2>
          <p className="text-[10px] text-[#A8A29E] uppercase tracking-wider mb-5" style={{ fontFamily: 'JetBrains Mono, monospace' }}>BY COUNTRY · STORES + SUBSCRIBERS</p>
          <div className="h-64">
            {regional.length === 0 ? (
              <div className="h-full flex items-center justify-center text-[#78716C] text-sm">No regional data yet</div>
            ) : (
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={regional} margin={{ top: 10, right: 10, left: 0, bottom: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#EDE6D8" vertical={false} />
                  <XAxis dataKey="country" tick={{ fontSize: 11, fontFamily: 'JetBrains Mono, monospace' }} stroke="#78716C" />
                  <YAxis tick={{ fontSize: 11, fontFamily: 'JetBrains Mono, monospace' }} stroke="#78716C" />
                  <Tooltip contentStyle={{ fontFamily: 'Plus Jakarta Sans, sans-serif', border: '1px solid #EDE6D8', borderRadius: '8px' }} />
                  <Legend wrapperStyle={{ fontSize: 11, fontFamily: 'JetBrains Mono, monospace' }} />
                  <Bar dataKey="stores" fill="#0A2540" name="Stores" radius={[6, 6, 0, 0]} />
                  <Bar dataKey="subscribers" fill="#10B981" name="Subscribers" radius={[6, 6, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            )}
          </div>
        </div>

        {/* Top 5 Entities by Stores (table) */}
        <div className="bg-[#FFFFFF] border border-[#EDE6D8] rounded-2xl p-6 shadow-[0_1px_3px_rgba(15,23,42,0.04)]" data-testid="stores-by-entity">
          <h2 className="text-sm font-semibold text-[#0A2540] tracking-tight mb-1" style={{ fontFamily: 'Plus Jakarta Sans, sans-serif' }}>Stores by Entity</h2>
          <p className="text-[10px] text-[#A8A29E] uppercase tracking-wider mb-4" style={{ fontFamily: 'JetBrains Mono, monospace' }}>TOP 5 ENTITIES</p>
          {topStoresByEntity.length === 0 ? (
            <div className="h-56 flex items-center justify-center">
              <p className="text-[#78716C] text-sm">No store data yet</p>
            </div>
          ) : (
            <div className="overflow-hidden rounded-xl border border-[#EDE6D8]" data-testid="stores-by-entity-table">
              <table className="w-full text-sm">
                <thead>
                  <tr className="bg-[#FCFAF6]">
                    <th className="text-left px-4 py-2.5 text-[10px] font-medium text-[#78716C] uppercase tracking-wider" style={{ fontFamily: 'JetBrains Mono, monospace' }}>#</th>
                    <th className="text-left px-4 py-2.5 text-[10px] font-medium text-[#78716C] uppercase tracking-wider" style={{ fontFamily: 'JetBrains Mono, monospace' }}>Entity</th>
                    <th className="text-right px-4 py-2.5 text-[10px] font-medium text-[#78716C] uppercase tracking-wider" style={{ fontFamily: 'JetBrains Mono, monospace' }}>Stores</th>
                    <th className="text-right px-4 py-2.5 text-[10px] font-medium text-[#78716C] uppercase tracking-wider" style={{ fontFamily: 'JetBrains Mono, monospace' }}>Share</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-[#EDE6D8]">
                  {topStoresByEntity.map((e, i) => {
                    const share = topStoresTotal > 0 ? (e.stores_count / topStoresTotal) * 100 : 0;
                    return (
                      <tr key={e.id} className="hover:bg-[#FCFAF6] transition-colors" data-testid={`stores-by-entity-row-${i}`}>
                        <td className="px-4 py-3 text-xs text-[#78716C] font-semibold" style={{ fontFamily: 'JetBrains Mono, monospace' }}>{i + 1}</td>
                        <td className="px-4 py-3">
                          <div className="flex items-center gap-2.5 min-w-0">
                            <div className="shrink-0 w-7 h-7 bg-[#0A2540] rounded-md flex items-center justify-center text-white text-[10px] font-semibold" style={{ fontFamily: 'JetBrains Mono, monospace' }}>
                              {(e.entity_code || '').substring(0, 2).toUpperCase()}
                            </div>
                            <div className="min-w-0">
                              <p className="text-sm font-medium text-[#1C1917] truncate" style={{ fontFamily: 'Plus Jakarta Sans, sans-serif' }}>{e.entity_name}</p>
                              <p className="text-[10px] text-[#A8A29E]" style={{ fontFamily: 'JetBrains Mono, monospace' }}>{e.entity_code || '—'} · {e.country_code || '—'}</p>
                            </div>
                          </div>
                        </td>
                        <td className="px-4 py-3 text-right text-sm font-bold text-[#0A2540]" style={{ fontFamily: 'JetBrains Mono, monospace' }}>{fmtInt(e.stores_count)}</td>
                        <td className="px-4 py-3 text-right text-xs text-[#78716C]" style={{ fontFamily: 'JetBrains Mono, monospace' }}>{share.toFixed(1)}%</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      {/* Entities Table */}
      <div className="bg-[#FFFFFF] border border-[#EDE6D8] rounded-2xl shadow-[0_1px_3px_rgba(15,23,42,0.04)]" data-testid="entities-table">
        <div className="p-6 border-b border-[#EDE6D8] flex items-center justify-between">
          <h2 className="text-sm font-semibold text-[#0A2540] tracking-tight" style={{ fontFamily: 'Plus Jakarta Sans, sans-serif' }}>All Entities</h2>
          <a href="/entities" className="inline-flex items-center gap-1 text-[10px] uppercase tracking-wider text-[#0A2540] font-semibold hover:underline" style={{ fontFamily: 'JetBrains Mono, monospace' }}>
            Manage <ArrowUpRight size={11} weight="bold" />
          </a>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="bg-[#FCFAF6]">
                {['Entity', 'Country', 'Campuses', 'Stores', 'Subscribers', 'Users', 'Status'].map((h) => (
                  <th key={h} className="text-left px-6 py-3 text-[10px] font-medium text-[#78716C] uppercase tracking-wider" style={{ fontFamily: 'JetBrains Mono, monospace' }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-[#EDE6D8]">
              {entitiesPageRows.map((entity) => (
                <tr key={entity.id} className="hover:bg-[#FCFAF6] transition-colors" data-testid={`entity-row-${entity.id}`}>
                  <td className="px-6 py-3">
                    <div className="flex items-center gap-3">
                      <div className="w-8 h-8 bg-[#0A2540] rounded-lg flex items-center justify-center text-white text-xs font-semibold" style={{ fontFamily: 'JetBrains Mono, monospace' }}>
                        {(entity.entity_code || '').substring(0, 2).toUpperCase()}
                      </div>
                      <div>
                        <p className="text-sm font-medium text-[#1C1917]" style={{ fontFamily: 'Plus Jakarta Sans, sans-serif' }}>{entity.entity_name}</p>
                        <p className="text-[10px] text-[#78716C]" style={{ fontFamily: 'JetBrains Mono, monospace' }}>{entity.entity_code}</p>
                      </div>
                    </div>
                  </td>
                  <td className="px-6 py-3">
                    <div className="flex items-center gap-2">
                      <Globe size={14} className="text-[#78716C]" />
                      <span className="text-sm text-[#1C1917]" style={{ fontFamily: 'JetBrains Mono, monospace' }}>{entity.country_code || '—'}</span>
                    </div>
                  </td>
                  <td className="px-6 py-3 text-sm text-[#1C1917]" style={{ fontFamily: 'JetBrains Mono, monospace' }}>{fmtInt(entity.campuses_count)}</td>
                  <td className="px-6 py-3 text-sm text-[#1C1917]" style={{ fontFamily: 'JetBrains Mono, monospace' }}>{fmtInt(entity.stores_count)}</td>
                  <td className="px-6 py-3 text-sm text-[#1C1917]" style={{ fontFamily: 'JetBrains Mono, monospace' }}>{fmtInt(entity.subscribers_count)}</td>
                  <td className="px-6 py-3 text-sm text-[#1C1917]" style={{ fontFamily: 'JetBrains Mono, monospace' }}>{fmtInt(entity.users_count)}</td>
                  <td className="px-6 py-3">
                    <span className={`inline-flex items-center px-2 py-1 text-[10px] font-medium rounded-md ${entity.status === 'active' ? 'bg-[#E6EEE7] text-[#10B981]' : 'bg-rose-50 text-[#EF4444]'}`} style={{ fontFamily: 'JetBrains Mono, monospace' }}>
                      {(entity.status || 'active').toUpperCase()}
                    </span>
                  </td>
                </tr>
              ))}
              {entities.length === 0 && (
                <tr><td colSpan={7} className="px-6 py-8 text-center text-sm text-[#78716C]">No entities</td></tr>
              )}
            </tbody>
          </table>
        </div>
        {entities.length > 0 && (
          <div className="flex items-center justify-between px-6 py-3 border-t border-[#EDE6D8] text-[11px]" style={{ fontFamily: 'JetBrains Mono, monospace' }} data-testid="entities-pagination">
            <span className="text-[#78716C] uppercase tracking-wider">
              {Math.min(entitiesStart + 1, entities.length)}-{Math.min(entitiesEnd, entities.length)} of {entities.length}
            </span>
            <div className="flex items-center gap-1">
              <button
                onClick={() => setEntitiesPage((p) => Math.max(1, p - 1))}
                disabled={entitiesPageSafe <= 1}
                data-testid="entities-prev"
                className="px-3 py-1.5 rounded-md border border-[#EDE6D8] text-[#0A2540] hover:bg-[#FCFAF6] disabled:opacity-40 disabled:cursor-not-allowed font-semibold uppercase tracking-wider"
              >
                Prev
              </button>
              <span className="px-3 py-1.5 text-[#0A2540] font-semibold">
                {entitiesPageSafe} / {entitiesTotalPages}
              </span>
              <button
                onClick={() => setEntitiesPage((p) => Math.min(entitiesTotalPages, p + 1))}
                disabled={entitiesPageSafe >= entitiesTotalPages}
                data-testid="entities-next"
                className="px-3 py-1.5 rounded-md border border-[#EDE6D8] text-[#0A2540] hover:bg-[#FCFAF6] disabled:opacity-40 disabled:cursor-not-allowed font-semibold uppercase tracking-wider"
              >
                Next
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
